import React, { useEffect, useState, useMemo, useRef } from "react";
import { HubShell } from "../components/hub/shell";
import AdminHeader from "../components/AdminHeader";
import ClientHeader from "../components/ClientHeader";
import NavSidebar from "../components/NavSidebar";
import { SkeletonCard, SkeletonText } from "../components/shared/Skeleton";
import { useNotification } from "../contexts/NotificationContext";
import { useActiveRole } from "../hooks/useActiveRole";
import { useTheme } from "../contexts/ThemeContext";
import {
  getUserDetails, 
  updateUser, 
  changePassword, 
  uploadAvatar, 
  removeAvatar, 
  updateThemePreference, 
  getActivityLog 
} from "../api";

const ProfilePage = ({ variant = "dashboard" }) => {
  const { showSuccess, showError, showConfirm, showWarning } = useNotification();
  // Epic 3 — ThemeContext is now the single source of truth for the APPLIED
  // theme (themeMode) and the only writer to document.documentElement +
  // localStorage('theme'). The profile page reads themeMode for the selected-
  // option highlight and calls setTheme() + updateThemePreference() on save;
  // it no longer manipulates the DOM or localStorage directly.
  const { themeMode, setTheme } = useTheme();
  const fileInputRef = useRef(null);
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  
  // Profile form
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });

  // Password form
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Avatar
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Theme — currentTheme now comes from ThemeContext.themeMode (the server
  // value pushed in by AuthContext), so there's no local state to seed here.
  // Only savingTheme remains local (the spinner state during the save).
  const [savingTheme, setSavingTheme] = useState(false);

  // Activity Log
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activityPagination, setActivityPagination] = useState(null);

  // Get user ID
  const userId = localStorage.getItem("user_id") || localStorage.getItem("client_id");
  // Multi-Org Membership Epic 1 — role follows the active org (not localStorage).
  const activeRole = useActiveRole();
  const username = localStorage.getItem("username") || "User";
  
  const initials = useMemo(() => {
    return username
      .split(' ')
      .map((n) => n[0]?.toUpperCase() || '')
      .join('')
      .slice(0, 2) || "U";
  }, [username]);

  const getRoleName = (role) => {
    switch (Number(role)) {
      case 0: return "Super Admin";
      case 1: return "Admin";
      case 2: return "Manager";
      case 3: return "Client";
      default: return "User";
    }
  };

  const getRoleColor = (role) => {
    switch (Number(role)) {
      case 0: return "from-red-500 to-rose-500";
      case 1: return "from-violet-500 to-purple-500";
      case 2: return "from-blue-500 to-cyan-500";
      case 3: return "from-emerald-500 to-teal-500";
      default: return "from-gray-500 to-slate-500";
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  useEffect(() => {
    if (activeTab === "activity") {
      fetchActivityLog();
    }
  }, [activeTab, activityPage]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await getUserDetails(userId);
      const userData = response?.data || response;
      setUser(userData);
      setFormData({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
      });
      setAvatarPreview(userData.avatarUrl || null);
      // currentTheme is owned by ThemeContext (seeded from the server by
      // AuthContext), so don't seed a local copy here — the highlight reads
      // themeMode directly.
    } catch (err) {
      showError("Error", err?.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLog = async () => {
    try {
      setActivityLoading(true);
      const response = await getActivityLog(activityPage, 15);
      setActivityLogs(response?.data?.logs || []);
      setActivityPagination(response?.data?.pagination || null);
    } catch (err) {
      console.error("Failed to fetch activity log:", err);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUser(userId, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
      });
      
      const displayName = [formData.first_name, formData.last_name].filter(Boolean).join(" ") || formData.email;
      localStorage.setItem("username", displayName);
      
      showSuccess("Success", "Profile updated successfully");
      setIsEditing(false);
      fetchUserDetails();
    } catch (err) {
      showError("Error", err?.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
    });
    setIsEditing(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      showError("Error", "New passwords do not match");
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      showError("Error", "Password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword(passwordData.current_password, passwordData.new_password);
      showSuccess("Success", "Password changed successfully");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      showError("Error", err?.message || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      showError("Error", "Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError("Error", "Image must be less than 5MB");
      return;
    }

    setUploadingAvatar(true);
    try {
      const response = await uploadAvatar(file);
      setAvatarPreview(response?.data?.avatar || null);
      localStorage.setItem("avatar", response?.data?.avatar || "");
      showSuccess("Success", "Profile picture updated");
      fetchUserDetails();
    } catch (err) {
      showError("Error", err?.message || "Failed to upload avatar");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = () => {
    showConfirm(
      "Remove Profile Picture",
      "Are you sure you want to remove your profile picture?",
      async () => {
        try {
          await removeAvatar();
          setAvatarPreview(null);
          localStorage.removeItem("avatar");
          showSuccess("Success", "Profile picture removed");
          fetchUserDetails();
        } catch (err) {
          showError("Error", err?.message || "Failed to remove avatar");
        }
      }
    );
  };

  const handleThemeChange = async (theme) => {
    setSavingTheme(true);
    try {
      // Epic 3 — single theme path. setTheme() applies the theme via
      // ThemeContext (the only writer to document.documentElement +
      // localStorage('theme')); updateThemePreference() persists to the server
      // (fn_update_my_profile) and confirms via the D1 read-back. No direct
      // classList / localStorage('theme_preference') manipulation here.
      setTheme(theme);
      const result = await updateThemePreference(theme);

      // D1 (read-back-for-certainty): on a silent no-op (confirmed:false +
      // warning) surface it to the user instead of a green "Theme updated".
      if (result?.confirmed === false && result?.warning) {
        showWarning("Theme not confirmed on server", result.warning);
      } else {
        showSuccess("Success", "Theme updated");
      }
    } catch (err) {
      showError("Error", err?.message || "Failed to update theme");
    } finally {
      setSavingTheme(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatActivityDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;
    
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const getActivityIcon = (action) => {
    switch (action) {
      case "login":
        return (
          <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
          </div>
        );
      case "password_changed":
        return (
          <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
        );
      case "avatar_updated":
      case "avatar_removed":
        return (
          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        );
      case "theme_changed":
        return (
          <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
          </div>
        );
      case "tag_changed":
        return (
          <div className="w-8 h-8 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
            <svg className="w-4 h-4 text-cyan-600 dark:text-cyan-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getInitials = () => {
    if (user?.first_name || user?.last_name) {
      return `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();
    }
    return (user?.display_name?.[0] || user?.email?.[0] || "U").toUpperCase();
  };

  const isClient = activeRole.isClient;
  // Agency-internal staff (isStaff) are Client-Hub-only and have no client
  // subscriptions, so the Subscriptions card (Chat/Voice Active/Inactive) is
  // hidden for them — it's a client-facing concept, not staff-facing.
  const isStaff = activeRole.isStaff;

  const tabs = [
    { id: "profile", label: "Profile", icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" },
    { id: "security", label: "Security", icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" },
    { id: "appearance", label: "Appearance", icon: "M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" },
    { id: "activity", label: "Activity", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
  ];

  const content = (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="space-y-6">
            <SkeletonCard lines={4} />
            <SkeletonCard lines={5} />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Profile Header Card */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border dark:border-gray-800 overflow-hidden">
              <div className={`h-32 bg-gradient-to-r ${getRoleColor(user?.role)}`}></div>
              
              <div className="px-6 pb-6">
                <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12">
                  {/* Avatar with upload */}
                  <div className="relative group">
                    <div className="w-24 h-24 rounded-2xl bg-white dark:bg-gray-800 border-4 border-white dark:border-gray-900 shadow-lg flex items-center justify-center overflow-hidden">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        <span className={`text-3xl font-bold bg-gradient-to-r ${getRoleColor(user?.role)} bg-clip-text text-transparent`}>
                          {getInitials()}
                        </span>
                      )}
                    </div>
                    
                    {/* Upload overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}>
                      {uploadingAvatar ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      ) : (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                    />
                    
                    {/* Remove avatar button */}
                    {avatarPreview && (
                      <button
                        onClick={handleRemoveAvatar}
                        className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove photo"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  <div className="flex-1 pb-2">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {[user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.display_name || user?.email}
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-3 py-1 text-sm font-medium rounded-full bg-gradient-to-r ${getRoleColor(user?.role)} text-white`}>
                        {getRoleName(user?.role)}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-1 flex gap-1 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-[100px] px-4 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                    activeTab === tab.id
                      ? "bg-[#32B1FF] text-white shadow"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d={tab.icon} />
                  </svg>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === "profile" && (
              <div className="space-y-6">
                {/* Personal Information */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border dark:border-gray-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Personal Information
                    </h2>
                    {!isEditing && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 bg-[#32B1FF] hover:bg-[#259ae6] text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Edit
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Name</label>
                          <input
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            className="w-full border dark:border-gray-700 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
                            placeholder="Enter first name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Name</label>
                          <input
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            className="w-full border dark:border-gray-700 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
                            placeholder="Enter last name"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full border dark:border-gray-700 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
                          placeholder="Enter email"
                        />
                      </div>

                      <div className="flex gap-3 pt-4">
                        <button onClick={handleCancel} className="flex-1 px-4 py-2.5 border dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 font-medium">
                          Cancel
                        </button>
                        <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 bg-[#32B1FF] hover:bg-[#259ae6] text-white rounded-lg disabled:opacity-50 font-medium">
                          {saving ? "Saving..." : "Save Changes"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500 dark:text-gray-400">First Name</p>
                        <p className="text-gray-900 dark:text-gray-100 font-medium">{user?.first_name || <span className="text-gray-400 italic">Not set</span>}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Last Name</p>
                        <p className="text-gray-900 dark:text-gray-100 font-medium">{user?.last_name || <span className="text-gray-400 italic">Not set</span>}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Email Address</p>
                        <p className="text-gray-900 dark:text-gray-100 font-medium">{user?.email}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Account & Subscriptions. Staff get only Account Details (the
                    Subscriptions card is client-facing and hidden for them), so
                    the grid collapses to a single column to avoid an empty
                    half-width slot. */}
                <div className={`grid gap-6 ${isStaff ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border dark:border-gray-800 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Account Details
                    </h2>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
                        <span className={`px-3 py-1 text-sm font-medium rounded-full ${user?.status === 1 ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"}`}>
                          {user?.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Member Since</span>
                        <span className="text-sm text-gray-900 dark:text-gray-100">{formatDate(user?.createdAt)?.split(",")[0]}</span>
                      </div>
                    </div>
                  </div>

                  {!isStaff && (
                    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border dark:border-gray-800 p-6">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                        </svg>
                        Subscriptions
                      </h2>
                      <div className="space-y-3">
                        <div className={`flex items-center gap-3 p-3 rounded-lg ${user?.is_chat_subscribed ? "bg-blue-50 dark:bg-blue-900/20" : "bg-gray-50 dark:bg-gray-800"}`}>
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${user?.is_chat_subscribed ? "bg-blue-500 text-white" : "bg-gray-300 dark:bg-gray-600 text-gray-500"}`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">Chat</p>
                            <p className={`text-xs ${user?.is_chat_subscribed ? "text-blue-600 dark:text-blue-400" : "text-gray-500"}`}>
                              {user?.is_chat_subscribed ? "Active" : "Inactive"}
                            </p>
                          </div>
                        </div>
                        <div className={`flex items-center gap-3 p-3 rounded-lg ${user?.is_voice_subscribed ? "bg-purple-50 dark:bg-purple-900/20" : "bg-gray-50 dark:bg-gray-800"}`}>
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${user?.is_voice_subscribed ? "bg-purple-500 text-white" : "bg-gray-300 dark:bg-gray-600 text-gray-500"}`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">Voice</p>
                            <p className={`text-xs ${user?.is_voice_subscribed ? "text-purple-600 dark:text-purple-400" : "text-gray-500"}`}>
                              {user?.is_voice_subscribed ? "Active" : "Inactive"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Company Info */}
                {user?.company && (
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border dark:border-gray-800 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      Company
                    </h2>
                    <div className="flex items-center gap-4">
                      {user.company.logoUrl ? (
                        <img src={user.company.logoUrl} alt="" className="w-14 h-14 rounded-xl object-cover border dark:border-gray-700" />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100">{user.company.name}</p>
                        {user.company.description && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{user.company.description}</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "security" && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border dark:border-gray-800 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Change Password
                </h2>

                <form onSubmit={handleChangePassword} className="max-w-md space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? "text" : "password"}
                        name="current_password"
                        value={passwordData.current_password}
                        onChange={handlePasswordChange}
                        required
                        className="w-full border dark:border-gray-700 rounded-lg px-4 py-2.5 pr-10 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          {showPasswords.current ? (
                            <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          ) : (
                            <>
                              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </>
                          )}
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? "text" : "password"}
                        name="new_password"
                        value={passwordData.new_password}
                        onChange={handlePasswordChange}
                        required
                        minLength={6}
                        className="w-full border dark:border-gray-700 rounded-lg px-4 py-2.5 pr-10 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          {showPasswords.new ? (
                            <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          ) : (
                            <>
                              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </>
                          )}
                        </svg>
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Confirm New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? "text" : "password"}
                        name="confirm_password"
                        value={passwordData.confirm_password}
                        onChange={handlePasswordChange}
                        required
                        className="w-full border dark:border-gray-700 rounded-lg px-4 py-2.5 pr-10 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          {showPasswords.confirm ? (
                            <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          ) : (
                            <>
                              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </>
                          )}
                        </svg>
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="w-full px-4 py-2.5 bg-[#32B1FF] hover:bg-[#259ae6] text-white rounded-lg disabled:opacity-50 font-medium mt-2"
                  >
                    {changingPassword ? "Changing Password..." : "Change Password"}
                  </button>
                </form>
              </div>
            )}

            {activeTab === "appearance" && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border dark:border-gray-800 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                  Theme Preference
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { id: "light", label: "Light", icon: "M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" },
                    { id: "dark", label: "Dark", icon: "M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" },
                    { id: "system", label: "System", icon: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },
                  ].map((theme) => (
                    <button
                      key={theme.id}
                      onClick={() => handleThemeChange(theme.id)}
                      disabled={savingTheme}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-3 ${
                        themeMode === theme.id
                          ? "border-[#32B1FF] bg-blue-50 dark:bg-blue-900/20"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        themeMode === theme.id
                          ? "bg-[#32B1FF] text-white"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                      }`}>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d={theme.icon} />
                        </svg>
                      </div>
                      <span className={`font-medium ${themeMode === theme.id ? "text-[#32B1FF]" : "text-gray-900 dark:text-gray-100"}`}>
                        {theme.label}
                      </span>
                      {themeMode === theme.id && (
                        <svg className="w-5 h-5 text-[#32B1FF]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>

                <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                  Choose your preferred color theme. "System" will automatically match your device's theme.
                </p>
              </div>
            )}

            {activeTab === "activity" && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border dark:border-gray-800 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  Activity Log
                </h2>

                {activityLoading ? (
                  <SkeletonText lines={6} lineClass="h-10" gapClass="space-y-2" className="py-2" />
                ) : activityLogs.length === 0 ? (
                  <div className="text-center py-12">
                    <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="text-gray-500 dark:text-gray-400">No activity recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activityLogs.map((log, idx) => (
                      <div key={log.id || idx} className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        {getActivityIcon(log.action)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {log.description || log.action.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          {log.entity_name && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {log.entity_type}: {log.entity_name}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                          {formatActivityDate(log.createdAt)}
                        </span>
                      </div>
                    ))}

                    {/* Pagination */}
                    {activityPagination && activityPagination.totalPages > 1 && (
                      <div className="flex items-center justify-between pt-4 border-t dark:border-gray-800">
                        <p className="text-sm text-gray-500">
                          Page {activityPagination.page} of {activityPagination.totalPages}
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setActivityPage(p => Math.max(1, p - 1))}
                            disabled={activityPage === 1}
                            className="px-3 py-1.5 text-sm border dark:border-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => setActivityPage(p => p + 1)}
                            disabled={activityPage >= activityPagination.totalPages}
                            className="px-3 py-1.5 text-sm border dark:border-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
  );

  // Hub variant — render inside the ClientHub shell (HubShell sidebar + top
  // bar) instead of the dashboard's AdminHeader + NavSidebar. Same content,
  // different chrome (mirrors EffortHubPage vs EffortPage).
  if (variant === "hub") {
    return <HubShell>{content}</HubShell>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {isClient ? (
        <ClientHeader username={username} initials={initials} activePage="Profile" />
      ) : (
        <AdminHeader activePage="Profile" />
      )}

      <div className="flex">
        <NavSidebar activePage={null} />
        <main className="flex-1 min-w-0">
          {content}
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;
